import {
  ADDITIONAL_TRANSFORM,
  cn,
  getTooltipStartingPosition,
  type TOOLTIP_POSITION,
} from '@exam-room/utils';
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps extends Omit<React.ComponentProps<'span'>, 'content'> {
  content: string | React.ReactNode;
  children: string | React.ReactNode;
  position?: `${TOOLTIP_POSITION}`;
  offset?: number;
}

function getArrowDirectionClasses(position: string): string {
  if (position.includes('t'))
    return 'border-t-grey-700 border-t-4 -top-[3px] left-6 -translate-x-1/2 border-r-4 border-l-4 border-r-transparent border-l-transparent';
  if (position.includes('b'))
    return 'border-b-grey-700 border-b-4 -top-[3px] left-6 -translate-x-1/2 border-r-4 border-l-4 border-r-transparent border-l-transparent';
  if (position.includes('l'))
    return 'border-l-grey-700 border-l-4 top-1/2 -left-1 -translate-y-1/2 border-t-4 border-b-4 border-t-transparent border-b-transparent';
  if (position.includes('r'))
    return 'border-r-grey-700 border-r-4 top-1/2 -right-1 -translate-y-1/2 border-t-4 border-b-4 border-t-transparent border-b-transparent';
  return 't';
}

export default function Tooltip({
  content,
  children,
  className,
  position = 't',
  offset = 5,
  ...props
}: TooltipProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [_position, setPosition] = useState<CSSProperties | undefined>(undefined);
  const [hover, setHover] = useState(false);

  const {
    width = 0,
    height = 0,
    top = 0,
    bottom = 0,
    left = 0,
    right = 0,
  } = containerRef.current?.getBoundingClientRect() ?? {};

  useEffect(() => {
    // get new starting position every time viewport changes
    const { x, y } = getTooltipStartingPosition({
      position,
      domReact: {
        width,
        height,
        top,
        bottom,
        left,
        right,
      },
      offset,
    });

    setPosition({ top: `${y}px`, left: `${x}px` });
  }, [width, height, top, bottom, left, right]);

  return (
    <div
      ref={containerRef}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {createPortal(
        <span
          style={_position || {}}
          className={cn(
            'bg-grey-700 rounded-md px-3 py-2 text-sm text-white',
            'pointer-events-none absolute z-10 w-fit opacity-0 transition-opacity',
            hover && 'opacity-100',
            ADDITIONAL_TRANSFORM[position],
            className,
          )}
          {...props}
        >
          {content}
          <span className={cn('absolute h-0 w-0', getArrowDirectionClasses(position))} />
        </span>,
        document.body,
      )}
      {children}
    </div>
  );
}
